/**
 * occupancy.ts — 車種別 月間売上（在庫管理ページ）の稼働率計算（2026-09-19 切り出し、09-22 実質台数）
 *
 *   稼働率   = 占有日数 ÷ 総容量日数
 *   総容量日数 = Σ(各車両の月内の稼働可能日数) − 稼働対象外日数
 *   実質台数  = 総容量日数 ÷ 月日数
 *   占有日数  = 通常予約 × 対象月との重なり日数 の合計
 *
 * 「実質台数」（2026-09-22 前田さん指示）: 月の途中で入った／抜けた車両は台数 1 ではなく、
 * 稼働可能だった日数の割合で数える。例: 31日の月に A,B=通月、C=1〜10日、D=1〜15日、E=16〜31日
 * → 1 + 1 + 10/31 + 15/31 + 16/31 ≒ 3.3 台。稼働率も平均/台もこの実質台数で割る。
 * 稼働可能日数は active_from / active_to だけで決める。is_active=false（非アクティブ＝意図的に
 * 予約を止めている予備車）や out_of_use も**リソースに含める**（以前は除外していた）。
 *
 * 予約種別が「米軍」「その他」の予約（lib/reservationKind.ts）は、車両の利用期間
 * （active_from / active_to）の外にいるのと同じ扱いにする。つまり
 *   - 占有日数に数えない（稼働していない）
 *   - その日数ぶん総容量日数からも引く（そもそも貸せる車として存在していない）
 * 米軍への長期貸出（部隊・代車）を稼働率に混ぜると、通常客への貸出実績が見えなくなるため。
 */
import dayjs from 'dayjs';
import { getReservationKind, DEFAULT_RESERVATION_KIND } from './reservationKind';

export interface MonthOccupancy {
  /** 通常予約が車両を占めた日数（車両ごとに稼働可能日数で上限クランプ） */
  occupiedDays: number;
  /** 米軍・その他の予約で稼働対象外になった日数（車両ごとに稼働可能日数で上限クランプ） */
  excludedDays: number;
  /** Σ稼働可能日数 − excludedDays（0 未満にはしない） */
  totalCapacityDays: number;
  /** 実質台数 = totalCapacityDays ÷ 月日数 */
  effectiveVehicles: number;
  /** 0〜100 */
  occupancyRate: number;
}

export interface OccupancyVehicle {
  id: number | string;
  active_from?: string | null;
  active_to?: string | null;
}

/**
 * 車両の月内の稼働可能日数（両端含む）。active_from / active_to が無ければ通月。
 * 月と重ならなければ 0。フラグ（is_active / out_of_use）は見ない
 */
export const vehicleActiveDaysInMonth = (v: OccupancyVehicle, month: dayjs.Dayjs): number => {
  const mStart = month.startOf('month');
  const mEnd = month.endOf('month');
  let s = mStart;
  let e = mEnd;
  if (v.active_from) {
    const from = dayjs(v.active_from);
    if (from.isValid() && from.isAfter(s)) s = from.startOf('day');
  }
  if (v.active_to) {
    const to = dayjs(v.active_to);
    if (to.isValid() && to.isBefore(e)) e = to.endOf('day');
  }
  if (s.isAfter(e)) return 0;
  return Math.min(e.diff(s.startOf('day'), 'day') + 1, month.daysInMonth());
};

/** 予約の期間と月の重なり日数（両端含む）。重ならなければ 0 */
export const overlapDaysInMonth = (
  startDate: string, endDate: string, month: dayjs.Dayjs,
): number => {
  const mStart = month.startOf('month');
  const mEnd = month.endOf('month');
  const rStart = dayjs(startDate);
  const rEnd = dayjs(endDate);
  if (!rStart.isValid() || !rEnd.isValid()) return 0;
  const s = rStart.isBefore(mStart) ? mStart : rStart;
  const e = rEnd.isAfter(mEnd) ? mEnd : rEnd;
  if (s.isBefore(e) || s.isSame(e, 'day')) {
    return Math.min(e.diff(s, 'day') + 1, month.daysInMonth());
  }
  return 0;
};

/** 稼働率の計算で「稼働対象外」とする予約か（米軍・その他） */
export const isOutOfServiceReservation = (reservation: { json_data?: unknown }): boolean =>
  getReservationKind(reservation.json_data) !== DEFAULT_RESERVATION_KIND;

/**
 * @param vehicles      その車種の車両（id / active_from / active_to）。月と重ならない車両は 0 台として扱われる
 * @param reservations  集計対象ステータスに絞った予約（vehicle_id / start_date / end_date / json_data）
 */
export const calcMonthOccupancy = (
  vehicles: OccupancyVehicle[],
  reservations: Array<{ vehicle_id?: number | string | null; start_date: string; end_date: string; json_data?: unknown }>,
  month: dayjs.Dayjs,
): MonthOccupancy => {
  const daysInMonth = month.daysInMonth();
  let occupiedDays = 0;
  let excludedDays = 0;
  let capacityDays = 0;

  vehicles.forEach((v) => {
    const activeDays = vehicleActiveDaysInMonth(v, month);
    if (activeDays <= 0) return;
    let vOccupied = 0;
    let vExcluded = 0;
    reservations.forEach((r) => {
      if (r.vehicle_id !== v.id) return;
      const days = overlapDaysInMonth(r.start_date, r.end_date, month);
      if (days <= 0) return;
      if (isOutOfServiceReservation(r)) vExcluded += days;
      else vOccupied += days;
    });
    // 1台がその月の稼働可能日数を超えることはない。同じ車に米軍の長期予約が二重に入っている
    // データ（実在する）で容量を引きすぎないよう、車両ごとに頭打ちにする
    vExcluded = Math.min(vExcluded, activeDays);
    vOccupied = Math.min(vOccupied, activeDays);
    capacityDays += activeDays - vExcluded;
    excludedDays += vExcluded;
    occupiedDays += vOccupied;
  });

  const totalCapacityDays = Math.max(capacityDays, 0);
  const rate = totalCapacityDays > 0 ? (occupiedDays / totalCapacityDays) * 100 : 0;
  return {
    occupiedDays,
    excludedDays,
    totalCapacityDays,
    effectiveVehicles: totalCapacityDays / daysInMonth,
    occupancyRate: Math.min(rate, 100),
  };
};
